// Phase 7a — AC#3: convert the 409 "Preset is immutable; clone first."
// dead-end toast into an actionable one.
//
// Every PATCH/DELETE against an `is_preset:true` row returns
//
//     409  {"detail": "Preset is immutable; clone first."}
//
// from the eight entity services. Operators previously saw a plain
// toast and had to remember to hit the Copy icon. We now inline a
// `Clone now` action on the toast that calls the same
// POST /api/<...>/<id>/clone the row's Copy button calls, then runs
// the entity-specific post-clone behaviour (auto-nav to Workbench
// for scenarios, refresh-and-select for terrain/obstacles, refresh
// only for the rest).

import { toast } from "sonner";
import {
    sim,
    terrain as terrainApi,
    obstacles as obstaclesApi,
} from "@/api/endpoints";

import { formatApiError } from "@/lib/formatApiError";
export const IMMUTABILITY_MESSAGE = "Preset is immutable; clone first.";

/** Recognise the canonical 409 shape emitted by all 8 entity services. */
export function isPresetImmutabilityError(err) {
    const status = err?.response?.status;
    if (status !== 409) return false;
    const detail = err?.response?.data?.detail;
    if (typeof detail !== "string") return false;
    return detail.includes("Preset is immutable");
}

// Single dispatch table for the 8 cross-cutting entity types. Each
// entry knows (a) how to call the entity's clone endpoint and (b)
// what to do with the cloned doc — match the existing Copy-icon flow
// on the parent page.
const CLONE_DISPATCH = {
    scenarios: {
        clone: (id) => sim.scenarios.clone(id),
        afterClone: (doc, ctx) => {
            // Mirrors ScenariosLibraryPage.handleClone — the
            // post-clone destination is the Workbench so the operator
            // can immediately tweak the tenant copy.
            if (ctx?.navigate) {
                ctx.navigate(`/workbench/${doc.id}#section=airframe`);
            } else {
                ctx?.onCloned?.(doc);
            }
        },
    },
    airframes: {
        clone: (id) => sim.airframes.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    environments: {
        clone: (id) => sim.environments.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    engagements: {
        clone: (id) => sim.engagements.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    "guidance-profiles": {
        clone: (id) => sim.guidanceProfiles.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    threats: {
        clone: (id) => sim.threats.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    terrain: {
        clone: (id) => terrainApi.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
    obstacles: {
        clone: (id) => obstaclesApi.clone(id),
        afterClone: (doc, ctx) => ctx?.onCloned?.(doc),
    },
};

export const CLONE_DISPATCH_ENTITY_TYPES = Object.keys(CLONE_DISPATCH);

/** Direct caller — the toast action button uses this. */
export async function cloneFromImmutabilityError(entityType, id, ctx = {}) {
    const entry = CLONE_DISPATCH[entityType];
    if (!entry) {
        throw new Error(`Unknown entityType for clone-on-409: ${entityType}`);
    }
    const doc = await entry.clone(id);
    entry.afterClone(doc, ctx);
    return doc;
}

/**
 * Surface the 409 toast with a `Clone now` action button. If the
 * error is *not* the canonical preset-immutability shape, fall back
 * to the supplied `fallback` (or a default toast.error). The
 * fallback path is tested explicitly so we don't accidentally widen
 * the helper into a generic error handler.
 *
 * @returns true when the immutability path was taken (toast + action
 *   rendered), false when the fallback ran.
 */
export function presetImmutabilityToast(err, opts = {}) {
    const {
        entityType,
        id,
        navigate,
        onCloned,
        fallback,
    } = opts;

    if (!isPresetImmutabilityError(err)) {
        if (typeof fallback === "function") {
            fallback(err);
        } else {
            const detail = formatApiError(err, "Update failed");
            toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
        return false;
    }

    if (!entityType || !CLONE_DISPATCH[entityType] || !id) {
        // We recognised the error but we don't have the wiring to
        // offer Clone now. Fail loud (but only as a no-action toast)
        // rather than silently swallow.
        toast.error(IMMUTABILITY_MESSAGE);
        return false;
    }

    toast.error(IMMUTABILITY_MESSAGE, {
        action: {
            label: "Clone now",
            onClick: async () => {
                try {
                    const doc = await cloneFromImmutabilityError(entityType, id, {
                        navigate,
                        onCloned,
                    });
                    toast.success(`Cloned to your tenant — id ${doc?.id || "?"}`);
                } catch (e) {
                    const detail =
                        formatApiError(e, "Clone failed");
                    toast.error(
                        typeof detail === "string" ? detail : JSON.stringify(detail),
                    );
                }
            },
        },
        duration: 8000,
    });
    return true;
}
